import { useState, useEffect, useCallback } from 'react';
import { 
  fetchPlatforms, 
  fetchPlatform, 
  createPlatform, 
  updatePlatform, 
  deletePlatform 
} from '../controller/platform-controller';

export const usePlatformViewModel = () => {
  // State declarations
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPlatform, setCurrentPlatform] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    password: '',
    typePlateform: '',
    fraisRetour: 0
  });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [platformToDelete, setPlatformToDelete] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [formErrors, setFormErrors] = useState({});
  
  // Pagination state based on API response structure
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [maxPage, setMaxPage] = useState(1);

  // Load platforms on initial render or when pagination changes
  useEffect(() => {
    loadPlatforms();
  }, [pageNumber, pageSize]);

  // Load platforms with pagination
  const loadPlatforms = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching platforms for page:', pageNumber, 'size:', pageSize);
      const response = await fetchPlatforms(pageNumber, pageSize);
      console.log('API Response:', response);
      
      // Handle paginated response structure
      if (response && response.list) {
        setPlatforms(response.list);
        
        // Update pagination info from the response
        // Only update these if they are defined in the response
        if (response.total !== undefined) {
          setTotalItems(response.total);
        }
        if (response.maxPage !== undefined) {
          setMaxPage(response.maxPage);
        }
      } else {
        // Fallback in case the response structure is different
        setPlatforms(Array.isArray(response) ? response : []);
      }
    } catch (err) {
      setError('Erreur lors du chargement des plateformes. Veuillez réessayer.');
      console.error('Error loading platforms:', err);
    } finally {
      setLoading(false);
    }
  }, [pageNumber, pageSize]);

  // Handle page change
  const handlePageChange = useCallback((newPage) => {
    console.log('Changing to page:', newPage);
    setPageNumber(newPage);
  }, []);

  // Handle page size change
  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize(newSize);
    setPageNumber(1); // Reset to first page when changing page size
  }, []);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  }, [formErrors]);

  const validateForm = useCallback(() => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Le nom de la plateforme est requis';
    }
    
    if (!formData.username.trim()) {
      errors.username = 'Le nom d\'utilisateur est requis';
    }
    
    if (!formData.typePlateform || !formData.typePlateform.trim()) {
      errors.typePlateform = 'Le type de plateforme est requis';
    }
    
    if (!currentPlatform && !formData.password.trim()) {
      errors.password = 'Le mot de passe est requis';
    } else if (!currentPlatform && formData.password.length < 6) {
      errors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, currentPlatform]);

  const handleOpenCreateModal = useCallback(() => {
    setCurrentPlatform(null);
    setFormData({
      name: '',
      username: '',
      password: '',
      typePlateform: '',
      fraisRetour: 0
    });
    setFormErrors({});
    setIsModalOpen(true);
  }, []);

  const handleOpenEditModal = useCallback((platform) => {
    setCurrentPlatform(platform);
    setFormData({
      name: platform.name || '',
      username: platform.username || '',
      password: '', // Usually don't pre-fill passwords in forms
      typePlateform: platform.typePlateform || '',
      fraisRetour: platform.fraisRetour ?? 0
    });
    setFormErrors({});
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setCurrentPlatform(null);
    setFormData({
      name: '',
      username: '',
      password: '',
      typePlateform: '',
      fraisRetour: 0
    });
    setFormErrors({});
  }, []);

  const handleSavePlatform = useCallback(async () => {
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      
      if (currentPlatform) {
        // Update existing platform
        const updatedData = { ...formData };
        // Only include password if it was changed
        if (!formData.password) {
          delete updatedData.password;
        }
        
        await updatePlatform(currentPlatform.id, updatedData);
        setSuccessMessage('Plateforme mise à jour avec succès');
      } else {
        // Create new platform
        await createPlatform(formData);
        setSuccessMessage('Plateforme créée avec succès');
      }
      
      // Reload platforms and close modal
      await loadPlatforms();
      handleCloseModal();
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      setError('Erreur lors de l\'enregistrement de la plateforme. Veuillez réessayer.');
      console.error('Error saving platform:', err);
    } finally {
      setLoading(false);
    }
  }, [formData, currentPlatform, validateForm, loadPlatforms, handleCloseModal]);

  const handleOpenDeleteModal = useCallback((platform) => {
    setPlatformToDelete(platform);
    setIsDeleteModalOpen(true);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false);
    setPlatformToDelete(null);
  }, []);

  const handleDeletePlatform = useCallback(async () => {
    if (!platformToDelete) return;
    
    try {
      setLoading(true);
      await deletePlatform(platformToDelete.id);
      
      // Reload platforms and close modal
      await loadPlatforms();
      handleCloseDeleteModal();
      
      setSuccessMessage('Plateforme supprimée avec succès');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      setError('Erreur lors de la suppression de la plateforme. Veuillez réessayer.');
      console.error('Error deleting platform:', err);
    } finally {
      setLoading(false);
    }
  }, [platformToDelete, loadPlatforms, handleCloseDeleteModal]);

  const dismissError = useCallback(() => {
    setError(null);
  }, []);

  const dismissSuccess = useCallback(() => {
    setSuccessMessage('');
  }, []);

  return {
    // State
    platforms,
    loading,
    error,
    successMessage,
    isModalOpen,
    currentPlatform,
    formData,
    formErrors,
    isDeleteModalOpen,
    platformToDelete,
    
    // Pagination
    pageNumber,
    pageSize,
    totalItems,
    maxPage,
    
    // Actions
    loadPlatforms,
    handleInputChange,
    handleOpenCreateModal,
    handleOpenEditModal,
    handleCloseModal,
    handleSavePlatform,
    handleOpenDeleteModal,
    handleCloseDeleteModal,
    handleDeletePlatform,
    dismissError,
    dismissSuccess,
    handlePageChange,
    handlePageSizeChange
  };
};